import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { api, CAPTURE_CATEGORIES } from "@/lib/caesar";
import { Labeled } from "@/components/common";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function QuickCapture({ open, onOpenChange }) {
  const [category, setCategory] = useState("Idea");
  const [description, setDescription] = useState("");
  const [projectId, setProjectId] = useState("");
  const [personId, setPersonId] = useState("");
  const [followup, setFollowup] = useState(false);
  const [owner, setOwner] = useState("");
  const [deadline, setDeadline] = useState("");
  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    api.get("/people").then(({ data }) => setPeople(data)).catch(() => {});
  }, [open]);

  const reset = () => {
    setCategory("Idea"); setDescription(""); setProjectId(""); setPersonId("");
    setFollowup(false); setOwner(""); setDeadline("");
  };

  const save = async () => {
    if (!description.trim()) { toast.error("Describe what happened."); return; }
    setSaving(true);
    try {
      const payload = {
        category, description, project_id: projectId, person_id: personId,
        requires_followup: followup,
        task_owner: owner,
        deadline: deadline ? new Date(deadline).toISOString() : "",
        priority_label: "normal",
      };
      const { data } = await api.post("/captures", payload);
      toast.success(data.task ? "Captured — task created" : "Captured");
      reset();
      onOpenChange(false);
    } catch {
      toast.error("Capture failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-surface border-edge max-w-lg" data-testid="capture-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">What happened?</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="flex flex-wrap gap-2">
            {CAPTURE_CATEGORIES.map((c) => (
              <button key={c} data-testid={`capture-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={cn("px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-150",
                  category === c ? "bg-gold text-ink border-gold" : "border-edge text-[#A3A3A3] hover:text-[#FAFAFA]")}>
                {c}
              </button>
            ))}
          </div>
          <Textarea data-testid="capture-description" autoFocus value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Type it fast… e.g. John promised $5,000 by Friday for boot camp"
            className="bg-ink border-edge min-h-[90px]" />
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Project">
              <select data-testid="capture-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} className="w-full h-9 rounded-md bg-ink border border-edge px-2 text-sm">
                <option value="">None</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Labeled>
            <Labeled label="Person">
              <select data-testid="capture-person" value={personId} onChange={(e) => setPersonId(e.target.value)} className="w-full h-9 rounded-md bg-ink border border-edge px-2 text-sm">
                <option value="">None</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Labeled>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-edge p-3">
            <span className="text-sm font-medium">Does this require follow-up?</span>
            <Switch data-testid="capture-followup" checked={followup} onCheckedChange={setFollowup} />
          </div>
          {followup && (
            <div className="grid grid-cols-2 gap-3 animate-fade-up">
              <Labeled label="Owner">
                <Input data-testid="capture-owner" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Who owns it?" className="bg-ink border-edge" />
              </Labeled>
              <Labeled label="Deadline">
                <Input data-testid="capture-deadline" type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="bg-ink border-edge" />
              </Labeled>
            </div>
          )}
          <button data-testid="capture-save" onClick={save} disabled={saving}
            className="w-full h-11 rounded-lg bg-gold text-ink font-bold hover:bg-[#e6c14e] transition-colors duration-150 disabled:opacity-60">
            {saving ? "Saving…" : "Save Capture"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
